import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Box, CircularProgress, Typography } from "@material-ui/core";
import { convertToBase64 } from "../../../../../../utils/convertToBase64";
import Text from "../../../../../shared/components/Text";

const DNI_ASPECT_RATIO = 85.6 / 53.98;

/**
 * On mobile the back camera should be used so the user can photograph
 * their document. On desktop/laptop the front (webcam) camera is used.
 */
const isMobileDevice = () =>
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );

const coverRectToSourcePixels = (
  intrinsicW,
  intrinsicH,
  layoutW,
  layoutH,
  rectX,
  rectY,
  rectW,
  rectH
) => {
  const scale = Math.max(layoutW / intrinsicW, layoutH / intrinsicH);
  const displayedW = intrinsicW * scale;
  const displayedH = intrinsicH * scale;
  const offsetX = (layoutW - displayedW) / 2;
  const offsetY = (layoutH - displayedH) / 2;

  let sx = (rectX - offsetX) / scale;
  let sy = (rectY - offsetY) / scale;
  let sw = rectW / scale;
  let sh = rectH / scale;

  sx = Math.max(0, Math.min(sx, intrinsicW - 1));
  sy = Math.max(0, Math.min(sy, intrinsicH - 1));
  sw = Math.min(sw, intrinsicW - sx);
  sh = Math.min(sh, intrinsicH - sy);

  return {
    sx: Math.round(sx),
    sy: Math.round(sy),
    sw: Math.max(1, Math.round(sw)),
    sh: Math.max(1, Math.round(sh)),
  };
};

const cameraStyles = {
  viewport: {
    position: "relative",
    width: "100%",
    maxWidth: 640,
    aspectRatio: "4 / 2.88",
    overflow: "hidden",
    borderRadius: 8,
    background: "#1a1a1a",
  },
  video: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    transition: "opacity 0.2s ease",
  },
  preparingOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "#1a1a1a",
    zIndex: 2,
  },
  mask: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
  },
  dniHole: {
    aspectRatio: DNI_ASPECT_RATIO,
    width: "min(88%, 400px)",
    minHeight: "78%",
    borderRadius: "3.71%",
    background: "transparent",
    boxShadow:
      "0 0 0 2px rgba(255, 255, 255, 0.85), 0 0 0 9999px rgba(0, 0, 0, 0.55)",
  },
  previewImg: {
    width: "100%",
    maxWidth: 640,
    objectFit: "contain",
    borderRadius: 8,
    background: "#1a1a1a",
    display: "block",
  },
};

export const DocumentIdCapture = forwardRef(({ onStateChange }, ref) => {
  const viewportRef = useRef(null);
  const holeRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [capturedBase64, setCapturedBase64] = useState(undefined);
  const [previewUrl, setPreviewUrl] = useState(undefined);
  const [videoReady, setVideoReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  // Back camera on mobile (document scan), front camera on desktop (webcam).
  const facingMode = isMobileDevice() ? "environment" : "user";

  const hasPicture = Boolean(capturedBase64 && previewUrl);

  const updateVideoReadyFromRef = useCallback(() => {
    const v = videoRef.current;
    setVideoReady(Boolean(v && v.videoWidth > 0 && v.videoHeight > 0));
  }, []);

  const onStateChangeRef = useRef(onStateChange);
  useEffect(() => {
    onStateChangeRef.current = onStateChange;
  }, [onStateChange]);

  useEffect(() => {
    onStateChangeRef.current?.({
      hasPicture,
      videoReady,
      capturedBase64,
      previewUrl,
      cameraError,
    });
  }, [hasPicture, videoReady, capturedBase64, previewUrl, cameraError]);

  // Re-attach getUserMedia when returning from preview: <video> unmounts while captured,
  // so a new element mounts on Retry and needs srcObject again ([] would only run once).
  useEffect(() => {
    if (hasPicture) {
      return undefined;
    }

    setVideoReady(false);
    setCameraError(null);

    const video = videoRef.current;
    if (!video) {
      return undefined;
    }

    let cancelled = false;

    async function startCamera() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        video.srcObject = stream;
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setVideoReady(false);
          setCameraError(e);
        }
      }
    }

    startCamera();

    return () => {
      cancelled = true;
      setVideoReady(false);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      video.srcObject = null;
    };
  }, [hasPicture, facingMode]);

  const takePicture = useCallback(() => {
    const video = videoRef.current;
    const viewport = viewportRef.current;
    const hole = holeRef.current;
    const canvas = canvasRef.current;
    if (!video || !viewport || !hole || !canvas) return;

    const iw = video.videoWidth;
    const ih = video.videoHeight;
    if (!iw || !ih) return;

    const layoutW = video.clientWidth;
    const layoutH = video.clientHeight;
    const vr = viewport.getBoundingClientRect();
    const hr = hole.getBoundingClientRect();
    const rectX = hr.left - vr.left;
    const rectY = hr.top - vr.top;
    const rectW = hr.width;
    const rectH = hr.height;

    const { sx, sy, sw, sh } = coverRectToSourcePixels(
      iw,
      ih,
      layoutW,
      layoutH,
      rectX,
      rectY,
      rectW,
      rectH
    );

    canvas.width = sw;
    canvas.height = sh;

    const ctx = canvas.getContext("2d");
    // Live preview is mirrored via CSS for easier positioning,
    // but the captured image must be readable (not mirrored).
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, sw, sh);

    const dataUrl = canvas.toDataURL("image/png");
    setPreviewUrl(dataUrl);

    canvas.toBlob((blob) => {
      if (!blob) return;
      convertToBase64(blob, (base64) => {
        setCapturedBase64(base64);
      });
    });
  }, []);

  const retryCapture = useCallback(() => {
    setCapturedBase64(undefined);
    setPreviewUrl(undefined);
    setCameraError(null);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      takePicture,
      retry: retryCapture,
      getCapturedBase64: () => capturedBase64,
      getVideoReady: () => videoReady,
      hasPicture: () => hasPicture,
    }),
    [takePicture, retryCapture, capturedBase64, videoReady, hasPicture]
  );

  return (
    <>
      {!hasPicture ? (
        <>
          <div id="camera-box" ref={viewportRef} style={cameraStyles.viewport}>
            <video
              ref={videoRef}
              style={{
                ...cameraStyles.video,
                opacity: videoReady ? 1 : 0,
                transform: facingMode === "user" ? "scaleX(-1)" : "none",
              }}
              autoPlay
              playsInline
              muted
              onLoadedMetadata={updateVideoReadyFromRef}
              onCanPlay={updateVideoReadyFromRef}
              onPlaying={updateVideoReadyFromRef}
            />
            {videoReady && (
              <div style={cameraStyles.mask} aria-hidden>
                <div ref={holeRef} style={cameraStyles.dniHole} />
              </div>
            )}
            {!videoReady && (
              <Box style={cameraStyles.preparingOverlay}>
                {cameraError ? (
                  <Box px={2} textAlign="center">
                    <Typography variant="body2" color="textSecondary">
                      <Text tId="ID_DOC_CAMERA_ERROR_BODY" />
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <CircularProgress color="primary" />
                    <Box mt={2} px={2} textAlign="center">
                      <Typography variant="body2" color="textSecondary">
                        <Text tId="PREPARING_CAMERA" />
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            )}
          </div>
        </>
      ) : (
        <>
          <img src={previewUrl} alt="" style={cameraStyles.previewImg} />
        </>
      )}
      <canvas ref={canvasRef} hidden />
    </>
  );
});
